import React, { useState, useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiActivity, FiCalendar } = FiIcons;

const RANGES = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: 'All', days: null },
];

function EmptyState() {
  return (
    <div className="bg-white rounded-3xl p-6 border border-[#E5E7EB] shadow-sm flex flex-col items-center text-center">
      <SafeIcon icon={FiActivity} className="text-3xl mb-2" style={{ color: '#9CA3AF' }} />
      <p className="text-sm font-semibold font-sans" style={{ color: '#1F2937' }}>Not enough data</p>
      <p className="text-xs font-sans mt-1" style={{ color: '#9CA3AF' }}>Log a few more days to unlock your glow trend.</p>
    </div>
  );
}

function RangePill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1 rounded-full text-xs font-sans font-medium border transition-all active:scale-95"
      style={
        active
          ? { background: 'linear-gradient(135deg, #CFEDEA 0%, #D6EAF8 50%, #E6DDF7 100%)', color: '#1F2937', borderColor: '#D6EAF8' }
          : { backgroundColor: '#F8FAFC', color: '#9CA3AF', borderColor: '#E5E7EB' }
      }
    >
      {label}
    </button>
  );
}

export default function AnalyticsCard({ entries }) {
  const [rangeIdx, setRangeIdx] = useState(1);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const allSorted = useMemo(
    () => [...(entries || [])].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [entries]
  );

  const chartData = useMemo(() => {
    if (!allSorted.length) return [];
    if (showCustom && customFrom && customTo) {
      const from = new Date(customFrom);
      const to = new Date(customTo);
      to.setHours(23, 59, 59);
      return allSorted.filter(e => {
        const d = new Date(e.date);
        return d >= from && d <= to;
      });
    }
    const days = RANGES[rangeIdx].days;
    if (!days) return allSorted;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return allSorted.filter(e => new Date(e.date) >= cutoff);
  }, [allSorted, rangeIdx, showCustom, customFrom, customTo]);

  const minDate = allSorted[0]?.date || '';
  const maxDate = allSorted[allSorted.length - 1]?.date || '';

  if (!entries || entries.length < 2) return <EmptyState />;

  const width = 300;
  const height = 130;
  const padX = 22;
  const padY = 15;
  const maxX = Math.max(1, chartData.length - 1);
  const getX = (i) => padX + (i / maxX) * (width - padX - 10);
  const getY = (s) => height - padY - ((s - 1) / 9) * (height - padY * 2);
  const points = chartData.map((d, i) => `${getX(i)},${getY(d.glowScore)}`).join(' L ');

  const hasAM = (e) => Array.isArray(e.routines) ? e.routines.includes('AM') : e.routines?.AM?.length > 0;
  const hasPM = (e) => Array.isArray(e.routines) ? e.routines.includes('PM') : e.routines?.PM?.length > 0;

  const habits = [
    { label: 'Optimal Hydration', check: (e) => (e.water || 0) >= 64 },
    { label: 'AM Routine', check: hasAM },
    { label: 'PM Routine', check: hasPM },
    { label: 'Supplements', check: (e) => e.supplements?.length > 0 },
  ];

  const highGlow = chartData.filter(e => e.glowScore >= 7);
  const lowGlow = chartData.filter(e => e.glowScore <= 5);
  const pos = [], neg = [];

  habits.forEach(h => {
    if (highGlow.length && highGlow.filter(h.check).length / highGlow.length >= 0.6) pos.push(h.label);
    if (lowGlow.length && 1 - lowGlow.filter(h.check).length / lowGlow.length >= 0.6) neg.push(`Skipped ${h.label}`);
  });

  if (!pos.length && highGlow.length) pos.push('Consistency');
  if (!neg.length && lowGlow.length) neg.push('Inconsistent routine');

  const avg = chartData.length
    ? (chartData.reduce((s, e) => s + e.glowScore, 0) / chartData.length).toFixed(1)
    : '—';

  const trend = chartData.length >= 2
    ? chartData[chartData.length - 1].glowScore - chartData[0].glowScore
    : 0;

  return (
    <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-sans text-xs" style={{ color: '#9CA3AF' }}>Avg Glow Score</p>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-3xl" style={{ color: '#1F2937' }}>{avg}</span>
              <span className="font-sans text-xs" style={{ color: '#9CA3AF' }}>/10</span>
              {trend !== 0 && (
                <span
                  className="font-sans text-xs font-semibold"
                  style={{ color: trend > 0 ? '#34D399' : '#F4C7C3' }}
                >
                  {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="font-sans text-xs" style={{ color: '#9CA3AF' }}>{chartData.length} entries</p>
          </div>
        </div>

        {/* Range Pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {RANGES.map((r, i) => (
            <RangePill
              key={r.label}
              label={r.label}
              active={!showCustom && rangeIdx === i}
              onClick={() => { setRangeIdx(i); setShowCustom(false); }}
            />
          ))}
          <button
            onClick={() => setShowCustom(v => !v)}
            className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-sans font-medium border transition-all active:scale-95"
            style={
              showCustom
                ? { background: 'linear-gradient(135deg, #E6DDF7 0%, #F6D5C9 100%)', color: '#1F2937', borderColor: '#E6DDF7' }
                : { backgroundColor: '#F8FAFC', color: '#9CA3AF', borderColor: '#E5E7EB' }
            }
          >
            <SafeIcon icon={FiCalendar} className="text-xs" />
            Custom
          </button>
        </div>

        {/* Custom Date Inputs */}
        {showCustom && (
          <div className="flex gap-2 mt-3">
            <div className="flex-1">
              <p className="font-sans text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#9CA3AF' }}>From</p>
              <input
                type="date"
                value={customFrom}
                min={minDate}
                max={customTo || maxDate}
                onChange={e => setCustomFrom(e.target.value)}
                className="w-full rounded-2xl px-3 py-2 text-xs font-sans focus:outline-none border"
                style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB', color: '#1F2937' }}
              />
            </div>
            <div className="flex-1">
              <p className="font-sans text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#9CA3AF' }}>To</p>
              <input
                type="date"
                value={customTo}
                min={customFrom || minDate}
                max={maxDate}
                onChange={e => setCustomTo(e.target.value)}
                className="w-full rounded-2xl px-3 py-2 text-xs font-sans focus:outline-none border"
                style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB', color: '#1F2937' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="px-4 pb-2">
        {chartData.length < 2 ? (
          <div className="flex flex-col items-center justify-center py-8" style={{ color: '#9CA3AF' }}>
            <SafeIcon icon={FiActivity} className="text-2xl mb-2" />
            <p className="font-sans text-xs">No entries in this range</p>
          </div>
        ) : (
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto overflow-visible">
            <defs>
              <linearGradient id="glowGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.18" />
                <stop offset="50%" stopColor="#60A5FA" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#CFEDEA" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="50%" stopColor="#60A5FA" />
                <stop offset="100%" stopColor="#34D399" />
              </linearGradient>
            </defs>

            {/* Grid */}
            {[10, 7, 4, 1].map(val => (
              <g key={val}>
                <line x1={padX} y1={getY(val)} x2={width - 5} y2={getY(val)} stroke="#E5E7EB" strokeWidth="1" strokeDasharray="3 3" />
                <text x={0} y={getY(val)} fontSize="9" fill="#9CA3AF" alignmentBaseline="middle" fontFamily="Inter, sans-serif">{val}</text>
              </g>
            ))}

            {/* Area */}
            <path
              d={`M ${getX(0)},${getY(1)} L ${points} L ${getX(chartData.length - 1)},${getY(1)} Z`}
              fill="url(#glowGrad)"
            />

            {/* Line */}
            <path d={`M ${points}`} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

            {/* Dots */}
            {chartData.map((d, i) => (
              <circle
                key={i}
                cx={getX(i)}
                cy={getY(d.glowScore)}
                r={chartData.length > 10 ? 3 : 4}
                fill="#fff"
                stroke={d.glowScore >= 8 ? '#A78BFA' : '#E5E7EB'}
                strokeWidth="2.5"
              />
            ))}
          </svg>
        )}
      </div>

      {/* Insights */}
      {chartData.length >= 2 && (
        <div className="grid grid-cols-2 gap-4 px-5 py-4 border-t border-[#E5E7EB]">
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-sm" style={{ color: '#34D399' }} />
              <span className="font-sans text-xs font-semibold" style={{ color: '#1F2937' }}>Glow Boosters</span>
            </div>
            <div className="space-y-1.5">
              {pos.length > 0
                ? pos.map((f, i) => <p key={i} className="text-xs font-sans leading-tight" style={{ color: '#9CA3AF' }}>• {f}</p>)
                : <p className="text-xs italic font-sans" style={{ color: '#9CA3AF' }}>Need more data</p>
              }
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <SafeIcon icon={FiTrendingDown} className="text-sm" style={{ color: '#F4C7C3' }} />
              <span className="font-sans text-xs font-semibold" style={{ color: '#1F2937' }}>Glow Drainers</span>
            </div>
            <div className="space-y-1.5">
              {neg.length > 0
                ? neg.map((f, i) => <p key={i} className="text-xs font-sans leading-tight" style={{ color: '#9CA3AF' }}>• {f}</p>)
                : <p className="text-xs italic font-sans" style={{ color: '#9CA3AF' }}>Need more data</p>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}